import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    // Check if app is already installed - more accurate detection
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isIOSStandalone = 'standalone' in window.navigator && window.navigator['standalone'] === true

    // Only consider it installed if it's actually in standalone mode
    if (isStandalone || isIOSStandalone) {
      return
    }

    // Don't use fullscreen mode as an indicator - it's not reliable
    // Fullscreen can be triggered by other things like video players

    // Check if launched from home screen (more reliable)
    const isLaunchedFromHomeScreen =
      (window.navigator as NavigatorWithStandalone).standalone === true ||
      (window.matchMedia('(display-mode: standalone)').matches && !window.matchMedia('(display-mode: browser)').matches)

    // Only mark as installed if we're confident it's actually installed
    if (isLaunchedFromHomeScreen) {
      return
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // DON'T call preventDefault() - this allows Chrome to show native install icon
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Let browser handle native install icon automatically
    }

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      setDeferredPrompt(null)
    }

    // Check PWA installability with Chrome-specific debugging
    const checkPWAInstallability = async () => {
      let isPWAInstallable = false
      const issues = []

      // Check service worker
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration()

          if (registration && registration.active) {
            isPWAInstallable = true
          } else {
            issues.push('Service worker not active')
          }
        } catch (error) {
          if (error instanceof Error) {
            issues.push('Service worker error: ' + error.message)
          } else {
            issues.push('Service worker error: ' + String(error))
          }
        }
      } else {
        issues.push('Service worker not supported')
      }

      // Check manifest
      try {
        const manifestResponse = await fetch('/site.webmanifest')
        if (!manifestResponse.ok) {
          throw new Error(`HTTP ${manifestResponse.status}: ${manifestResponse.statusText}`)
        }
        const manifest = await manifestResponse.json()

        // Check if manifest has required fields for Chrome
        const requiredFields = ['name', 'short_name', 'start_url', 'display', 'icons']
        const missingFields = requiredFields.filter((field) => !manifest[field])

        if (missingFields.length > 0) {
          issues.push('Manifest missing fields: ' + missingFields.join(', '))
        } else if (manifest.icons && manifest.icons.length > 0) {
          isPWAInstallable = true
        } else {
          issues.push('Manifest has no icons')
        }
      } catch (error) {
        if (error instanceof Error) {
          issues.push('Manifest error: ' + error.message)
        } else {
          issues.push('Manifest error: ' + String(error))
        }
      }

      // Check if PWA meets installability criteria
      const isHTTPS = location.protocol === 'https:' || location.hostname === 'localhost'
      const hasManifest = document.querySelector('link[rel="manifest"]') !== null
      const isSecureContext = window.isSecureContext

      return isPWAInstallable && isHTTPS && hasManifest && isSecureContext
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Only check installability for debugging - don't show custom prompt
    const checkInstallability = async () => {
      const isInstallable = await checkPWAInstallability()

      // Chrome-specific debugging and user engagement triggers
      if (isInstallable && !deferredPrompt) {
        // Chrome-specific user engagement requirements
        if (/Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent)) {
          // Chrome requires user engagement before showing install prompt
          const triggerChromeEngagement = () => {
            // Real user interactions that Chrome recognizes
            const events = ['click', 'touchstart', 'touchend', 'keydown', 'mousedown', 'mouseup']

            events.forEach((eventType) => {
              const event = new Event(eventType, { bubbles: true, cancelable: true })
              document.dispatchEvent(event)
            })

            // Scroll interaction (Chrome likes this)
            window.scrollBy(0, 1)
            setTimeout(() => window.scrollBy(0, -1), 100)

            // Focus/blur cycle
            window.focus()
            setTimeout(() => window.blur(), 50)
            setTimeout(() => window.focus(), 100)
          }

          // Trigger engagement multiple times with delays
          triggerChromeEngagement()
          setTimeout(triggerChromeEngagement, 2000)
          setTimeout(triggerChromeEngagement, 5000)
          setTimeout(triggerChromeEngagement, 10000)

          // Add click listener to any element to trigger engagement
          const addEngagementListener = () => {
            document.addEventListener(
              'click',
              () => {
                //
              },
              { once: true }
            )
          }
          addEngagementListener()
        }
      }

      return isInstallable
    }

    // Run installability check for debugging
    checkInstallability()

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // Function to reset install prompt preferences (useful for testing)
  const resetInstallPrompt = () => {
    localStorage.removeItem('pwa-install-dismissed')
    localStorage.removeItem('pwa-install-remind')
  }

  // Expose reset function globally for testing (remove in production)
  if (typeof window !== 'undefined') {
    ;(
      window as unknown as {
        resetInstallPrompt?: () => void
        debugPWA?: () => void
        showInstallPrompt?: () => void
      }
    ).resetInstallPrompt = resetInstallPrompt

    // Debug function to check PWA state
    ;(
      window as unknown as {
        resetInstallPrompt?: () => void
        debugPWA?: () => void
        showInstallPrompt?: () => void
      }
    ).debugPWA = () => {
      //
    }

    // Manual trigger to show install prompt
    ;(
      window as unknown as {
        resetInstallPrompt?: () => void
        debugPWA?: () => void
        showInstallPrompt?: () => void
      }
    ).showInstallPrompt = () => {
      //
    }
  }

  // Don't render anything if no install prompt available
  return null
}
